import React from "react";
import { motion } from "framer-motion";
import * as Dialog from "@radix-ui/react-dialog";

/**
 * Completed development milestones displayed in the roadmap.
 * @type {Array<{status: string, title: string, desc: string}>}
 */
const DONE_ITEMS = [
  {
    status: "Done",
    title: "Checking consistency",
    desc: "Have to check the entire dataset for consistency in the writing/formatting",
  },
  {
    status: "Done",
    title: "Adding current progress to sidebar",
    desc: "Want to add the current progress of each mode to the sidebar so people can see how they're doing like in the OG",
  },
  {
    status: "Done",
    title: "Rotating background wallpapers",
    desc: "Bringing back the rotating background wallpapers like the OG",
  },
  {
    status: "Done",
    title: "Character icons",
    desc: "Bringing back the character icons everywhere like the OG",
  },
  {
    status: "Done",
    title: "Collection overhaul",
    desc: "Overhauling the collection to show every category not just characters",
  },
  {
    status: "Done",
    title: "UI Update",
    desc: "I overhauled the game's UI with a fully themed Orbal Era design system including unique aesthetics for each era",
  },
  {
    status: "Done",
    title: "UI tweaks",
    desc: "A bunch of people have sent in feedback about the UI, so I am gonna be working on those tweaks - thank you for your feedback! :D",
  },
];

/**
 * Upcoming / in-progress development items.
 * @type {Array<{status: string, title: string, desc: string}>}
 */
const ROADMAP_ITEMS = [
  {
    status: "In Progress",
    title: "Adding content for locations mode",
    desc: "We want to add content for Locations as it has the least amount of quizzes by far",
  },
  {
    status: "Not started",
    title: "Adding Horizon content",
    desc: "After the locations update we will be adding Horizon content to all modes, as promised",
  },
  {
    status: "Not started",
    title: "Infinite Mode",
    desc: "Making an Infinite or Manual Mode for people that don't want to wait for next day",
  },
  {
    status: "Not started",
    title: "More content",
    desc: "We'll be asking for suggestions after being done with all other updates for more content and will add them as we get them (Art Mode has been mentioned so far)",
  },
];

/**
 * Radix dialog showing the development roadmap — split into upcoming ("In Progress"
 * / "Not Started") and "Done" sections with staggered framer-motion animations.
 *
 * @param {{ onClose: () => void, currentTheme: string }} props
 * @param {() => void} props.onClose - Callback to dismiss the dialog.
 * @param {string} props.currentTheme - Active era theme name for overlay/container styling.
 * @returns {JSX.Element} The roadmap modal dialog.
 */
function RoadmapModal({ onClose, currentTheme }) {
  return (
    <Dialog.Root open={true} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay forceMount asChild>
          <motion.div
            className={`modal-overlay theme-${currentTheme}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
        </Dialog.Overlay>
        <div className={`modal-content-wrapper theme-${currentTheme}`}>
          <Dialog.Content forceMount asChild>
            <motion.div
              className="modal-content roadmap-modal-content"
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: "spring", stiffness: 300, damping: 25 }}
            >
              <Dialog.Title className="sr-only">Development Roadmap</Dialog.Title>
              <Dialog.Description className="sr-only">
                Current and upcoming features for Kisekidle.
              </Dialog.Description>

              <Dialog.Close asChild>
                <button className="modal-close">
                  ✖
                </button>
              </Dialog.Close>

              <h2 className="modal-title">🗺️ Development Roadmap</h2>
              <p className="modal-subtitle">
                Here is what is currently worked on in the Central Factory - If your
                idea/bug is already on this list, no need to report it!
              </p>

              <div className="roadmap-list">
                {ROADMAP_ITEMS.map((item, index) => {
                  const badgeClass =
                    item.status === "In Progress"
                      ? "status-progress"
                      : "status-not-started";

                  return (
                    <motion.div
                      key={index}
                      className="roadmap-item"
                      initial={{ opacity: 0, x: -15 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.2 + index * 0.1 }}
                    >
                      <div className={`roadmap-status ${badgeClass}`}>
                        {item.status}
                      </div>
                      <div className="roadmap-text">
                        <h4>{item.title}</h4>
                        <p>{item.desc}</p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>

              <div className="roadmap-done-section">
                <hr className="roadmap-divider" />
                <h3 className="modal-title" style={{ fontSize: "1.1rem", marginBottom: "15px" }}>✅ Done</h3>
                {DONE_ITEMS.map((item, index) => (
                  <motion.div
                    key={index}
                    className="roadmap-item roadmap-item-done"
                    initial={{ opacity: 0, x: -15 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4 + index * 0.05 }}
                  >
                    <div className="roadmap-status status-done">
                      {item.status}
                    </div>
                    <div className="roadmap-text">
                      <h4>{item.title}</h4>
                      <p>{item.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </Dialog.Content>
        </div>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export default RoadmapModal;
